import React, { useState, useEffect } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { Container, Card, Row, Col, Button, Badge, Modal, Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowLeft,
  faEdit,
  faTrashAlt,
  faInfoCircle,
  faHashtag,
  faCalendarAlt,
  faUser,
  faServer
} from '@fortawesome/free-solid-svg-icons';
// Using project Utility for toast notifications
import { getVariantOptionDetails, deleteVariantOption } from '../../Controller/VariantOptionController';
import Utility from '../../../../../GlobalModules/Utility/Utility';
import ConfirmationAlert from '../../../../../GlobalModules/Utility/Components/ConfirmationAlert';
import { connect } from 'react-redux';
import { withNamespaces } from 'react-i18next';

import '../../Assets/css/variant-options.scss';

/**
 * Component to view variant option details
 * @returns {JSX.Element} VariantOptionView component
 */
const VariantOptionView = (props) => {
  const { slack, lng } = useParams();

  // State for variant option details
  const [variantOption, setVariantOption] = useState({});
  const [loading, setLoading] = useState(true);

  // State for delete confirmation
  const [showDeleteAlert, setShowDeleteAlert] = useState(false);

  // Fetch variant option details on component mount
  useEffect(() => {
    fetchVariantOptionDetails();
  }, [slack]);

  /**
   * Fetch variant option details
   */
  const fetchVariantOptionDetails = async () => {
    setLoading(true);
    try {
      const response = await getVariantOptionDetails(slack);

      if (response.success && response.data) {
        setVariantOption(response.data);
      } else {
        Utility.toastNotifications(response.message || 'Failed to fetch variant option details', 'Error', 'error');
      }
    } catch (error) {
      console.error('Error fetching variant option details:', error);
      Utility.toastNotifications('An error occurred while fetching variant option details', 'Error', 'error');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Confirm delete operation
   */
  const confirmDelete = () => {
    setShowDeleteAlert(true);
  };

  /**
   * Cancel delete operation
   */
  const cancelDelete = () => {
    setShowDeleteAlert(false);
  };

  /**
   * Handle delete operation after confirmation
   */
  const handleDelete = async () => {
    try {
      const response = await deleteVariantOption(slack);

      if (response.success) {
        Utility.toastNotifications(response.message || 'Variant option deleted successfully', 'Success', 'success');
        history.push(`/${lng}/settings/variant-options`);
      } else {
        Utility.toastNotifications(response.message || 'Failed to delete variant option', 'Error', 'error');
      }
    } catch (error) {
      console.error('Error deleting variant option:', error);
      Utility.toastNotifications('An error occurred while deleting variant option', 'Error', 'error');
    } finally {
      setShowDeleteAlert(false);
    }
  };

  const history = useHistory();

  //======= add action permission ====//
  // Check multiple permissions at once
  const variantOptionViewPermissions = Utility.hasMultipleActionPermissions(
    'settings',
    'variant_options',
    ['view', 'create', 'edit', 'delete', 'export', 'approve', 'receive', 'verify', 'reject', 'verify_accept'],
    props?.userCredentials?.user_details?.user_type || ['admin', 'app_admin']
  );

  if (loading) {
    return (
      <Container fluid className='p-0'>
        <div
          className='d-flex justify-content-center align-items-center'
          style={{ minHeight: '400px' }}
        >
          <Spinner animation='border' variant='primary' />
          <span className='ms-2'>Loading variant option details...</span>
        </div>
      </Container>
    );
  }

  return (
    <Container fluid className='p-0 variant-option-view-page'>
      <Row className='top-header-box variant-option-view-header mb-3'>
        <Col className='variant-option-header-content'>
          <div className='variant-option-title-row'>
            <h4 className='variant-option-title'>
              {variantOption.label || 'Variant Option'}
            </h4>
            <Badge
              bg={variantOption.status === 1 ? 'success' : 'danger'}
              className='badgeCustom'
            >
              {variantOption.status_data?.label ||
                (variantOption.status === 1 ? 'Active' : 'Inactive')}
            </Badge>
          </div>
        </Col>
        <Col xs='auto' className='variant-option-header-actions'>
          {variantOptionViewPermissions.edit && (
            <Button
              variant='outline-primary'
              onClick={() => history.push(`/${lng}/settings/variant-options/edit/${slack}`)}
            >
              <FontAwesomeIcon icon={faEdit} className='me-1' />
              Edit
            </Button>
          )}
          {variantOptionViewPermissions.delete && (
            <Button variant='danger' onClick={confirmDelete}>
              <FontAwesomeIcon icon={faTrashAlt} className='me-1' />
              Delete
            </Button>
          )}
          <Button
            variant='outline-secondary'
            onClick={() => history.push(`/${lng}/settings/variant-options`)}
          >
            <FontAwesomeIcon icon={faArrowLeft} className='me-1' />
            Back to List
          </Button>
        </Col>
      </Row>

      <Row>
        <Col>
          <Card className='custom-border-light mb-4 main-content-card variant-option-details-card'>
            <Card.Header>
              <h6>
                <FontAwesomeIcon icon={faInfoCircle} className='me-2' />
                Basic Information
              </h6>
            </Card.Header>
            <Card.Body>
              <div className='variant-option-details-grid'>
                <div className='detail-item'>
                  <span className='detail-icon'>
                    <FontAwesomeIcon icon={faHashtag} />
                  </span>
                  <div className='detail-content'>
                    <span className='detail-label'>Variant Option Code</span>
                    <span className='detail-value'>
                      {variantOption.variantOptionCode || 'N/A'}
                    </span>
                  </div>
                </div>
                <div className='detail-item'>
                  <span className='detail-icon'>
                    <FontAwesomeIcon icon={faInfoCircle} />
                  </span>
                  <div className='detail-content'>
                    <span className='detail-label'>Label</span>
                    <span className='detail-value'>
                      {variantOption.label || 'N/A'}
                    </span>
                  </div>
                </div>
                <div className='detail-item'>
                  <span className='detail-icon'>
                    <FontAwesomeIcon icon={faInfoCircle} />
                  </span>
                  <div className='detail-content'>
                    <span className='detail-label'>Status</span>
                    <span className='detail-value'>
                      <Badge
                        bg={variantOption.status === 1 ? 'success' : 'danger'}
                        className='badgeCustom'
                      >
                        {variantOption.status_data?.label ||
                          (variantOption.status === 1 ? 'Active' : 'Inactive')}
                      </Badge>
                    </span>
                  </div>
                </div>
              </div>
            </Card.Body>
          </Card>

          <Card className='custom-border-light mb-4 main-content-card'>
            <Card.Header>
              <h6>
                <FontAwesomeIcon icon={faServer} className='me-2' />
                System Information
              </h6>
            </Card.Header>
            <Card.Body>
              <div className='variant-option-details-grid'>
                <div className='detail-item'>
                  <span className='detail-icon'>
                    <FontAwesomeIcon icon={faHashtag} />
                  </span>
                  <div className='detail-content'>
                    <span className='detail-label'>ID</span>
                    <span className='detail-value'>
                      {variantOption.id || 'N/A'}
                    </span>
                  </div>
                </div>
                <div className='detail-item'>
                  <span className='detail-icon'>
                    <FontAwesomeIcon icon={faCalendarAlt} />
                  </span>
                  <div className='detail-content'>
                    <span className='detail-label'>Created On</span>
                    <span className='detail-value'>
                      {Utility.get12HourFormat(variantOption.createdAt) || 'N/A'}
                    </span>
                  </div>
                </div>
                <div className='detail-item'>
                  <span className='detail-icon'>
                    <FontAwesomeIcon icon={faCalendarAlt} />
                  </span>
                  <div className='detail-content'>
                    <span className='detail-label'>Last Updated</span>
                    <span className='detail-value'>
                      {Utility.get12HourFormat(variantOption.updatedAt) || 'N/A'}
                    </span>
                  </div>
                </div>
                <div className='detail-item'>
                  <span className='detail-icon'>
                    <FontAwesomeIcon icon={faUser} />
                  </span>
                  <div className='detail-content'>
                    <span className='detail-label'>Created By</span>
                    <span className='detail-value'>
                      {variantOption.CreatedUser
                        ? `${variantOption.CreatedUser.first_name} ${variantOption.CreatedUser.last_name}`
                        : 'N/A'}
                    </span>
                  </div>
                </div>
                <div className='detail-item'>
                  <span className='detail-icon'>
                    <FontAwesomeIcon icon={faUser} />
                  </span>
                  <div className='detail-content'>
                    <span className='detail-label'>Updated By</span>
                    <span className='detail-value'>
                      {variantOption.UpdatedUser
                        ? `${variantOption.UpdatedUser.first_name} ${variantOption.UpdatedUser.last_name}`
                        : 'N/A'}
                    </span>
                  </div>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteAlert} onHide={cancelDelete} centered>
        <Modal.Header closeButton>
          <Modal.Title>Confirmation</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <ConfirmationAlert
            BodyFirstContent="Are you sure you want to delete this"
            BodySecondContent={variantOption.label}
            BodyThirdContent="variant option?"
            confirmationButtonContent="Yes, delete it!"
            cancelButtonContent="Cancel"
            deleteIconVissable={true}
            deleteConfirmButton={handleDelete}
            deleteCancleButton={cancelDelete}
          />
        </Modal.Body>
      </Modal>
    </Container>
  );
};

const mapStateToProps = (globalState) => {
  return {
    userCredentials: globalState.LoginReducer.userCredentials,
    token: globalState.LoginReducer.token
  };
}

export default connect(mapStateToProps)(withNamespaces()(VariantOptionView));

